// ResourceDetail's pure half: the kind-aware "couldn't load" copy, a file's
// facts and the promote intent. No I/O, no casts.

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { IconName } from "../../components/Icon/Icon.js";
import type { SearchResultKind } from "../../components/Search/searchModel.js";
import type { LoadedFile } from "../ports/resource.js";

// The unavailable state names WHAT failed to load, with that kind's own icon.
export type UnavailableCopy = {
    readonly icon: IconName;
    readonly title: MessageDescriptor;
    readonly body: MessageDescriptor;
};

const BODY = msg`It may have been removed, or isn't available right now.`;

export const unavailableCopy = (kind: SearchResultKind): UnavailableCopy => {
    switch (kind) {
        case "source":
            return {
                icon: "source",
                title: msg`This source couldn't be loaded`,
                body: BODY,
            };
        case "material":
            return {
                icon: "material",
                title: msg`This material couldn't be loaded`,
                body: BODY,
            };
        case "package":
            return {
                icon: "export",
                title: msg`This package couldn't be loaded`,
                body: BODY,
            };
        case "file":
            return {
                icon: "file",
                title: msg`This file couldn't be loaded`,
                body: BODY,
            };
        case "conversation":
            return {
                icon: "chat",
                title: msg`This conversation couldn't be loaded`,
                body: BODY,
            };
    }
};

// === File detail ============================================================
// The known facts about a File, in display order; unknown ones are omitted
// (never a placeholder). The view labels each and localizes the date.
export type FileFact =
    | { readonly type: "media-type"; readonly mediaType: string }
    | { readonly type: "created"; readonly iso: string }
    | { readonly type: "description"; readonly text: string };

export const fileFacts = (file: LoadedFile): readonly FileFact[] => {
    const facts: (FileFact | undefined)[] = [
        file.mediaType === undefined
            ? undefined
            : { type: "media-type", mediaType: file.mediaType },
        file.createdAt === undefined
            ? undefined
            : { type: "created", iso: file.createdAt },
        file.description === undefined
            ? undefined
            : { type: "description", text: file.description },
    ];
    return facts.filter((f): f is FileFact => f !== undefined);
};

// === Make a file a source ===================================================
// The form's one intent: promote the File with the author's text about it
// (core `PromoteFileToSourceManually`). The new source takes the file's title.
export type PromoteFileToSourceIntent = {
    readonly type: "promote-file-to-source";
    readonly fileId: string;
    readonly title: string;
    readonly annotation: string;
};

// The submitted form → its intent, or null while the text is blank (the
// confirm stays disabled; the domain would only store an empty source).
export const promoteIntentOf = (
    fileId: string,
    title: string,
    draft: string,
): PromoteFileToSourceIntent | null => {
    const annotation = draft.trim();
    return annotation === ""
        ? null
        : { type: "promote-file-to-source", fileId, title, annotation };
};

// How the host's run of the intent ended, for the form to settle on. On
// success the host has already selected the new source, so the form unmounts.
export type PromoteFileOutcome =
    | { readonly type: "promoted" }
    | { readonly type: "failed"; readonly message: string };
